import { t } from "adapters/i18n";
import transformerUtils from "shared/transformerUtils";
import type { Corral, Curral, Pasto, Pasture } from "shared/types";
import BaseService from "../BaseService";
import type { DataResult } from "../services.types";
import { composeMixins } from "../services.utils";
import { RetreatServiceMixin } from "./retreatService";

type PastureRelations = "module" | "feedingTroughs" | "retreat";

export class PastureService extends composeMixins(RetreatServiceMixin)(
  BaseService
) {
  readonly pastureUrl = `${process.env.REACT_APP_ENDPOINT}pastos/api/v1/pastos`;

  async listPasturesForFarm(
    farmId: number,
    options: { with?: PastureRelations[] } = {}
  ): Promise<DataResult<Pasture>> {
    try {
      const relationMap: Record<PastureRelations, string> = {
        module: "Modulo",
        feedingTroughs: "Cocho",
        retreat: "Retiro",
      };

      const plainFilter = {
        where: { IdFazenda: farmId },
        include: options.with?.length
          ? options.with.map((relation) => relationMap[relation])
          : [],
      };

      const filter = this.montaFiltro(plainFilter);
      const url = this.montaURL(this.pastureUrl, filter);
      const result = await this.get(url);

      if (result.data == null) {
        return { data: [], total: 0 };
      }

      const pastures = (result.data as Pasto[]).map((item) =>
        transformerUtils.toPasture(item)
      );

      const total = pastures.length;

      return { data: pastures, total };
    } catch (_) {
      throw new Error(t("locales:fetch-farm-pastures-error"));
    }
  }

  async listCorralsForFarm(
    farmId: number,
    options: { with?: PastureRelations[] } = {}
  ): Promise<DataResult<Corral>> {
    try {
      const pastures = await this.listPasturesForFarm(farmId, options);

      const corrals = pastures.data.filter(
        (item): item is Corral =>
          item.isCorral === true && item.coordinates == null
      );

      return { data: corrals, total: corrals.length };
    } catch (_) {
      throw new Error(t("locales:fetch-farm-corrals-error"));
    }
  }

  async createCorral(
    payload: Pick<Corral, "farmId" | "name" | "shortName" | "retreatId">
  ): Promise<Corral> {
    try {
      const payloadData = {
        AreaAberta: null,
        AreaEfetiva: null,
        Coordenadas: null,
        Curral: true,
        IdFazenda: payload.farmId,
        IdForragem: null,
        IdModulo: null,
        IdPasto: null,
        IdRetiro: payload.retreatId,
        Nome: payload.name,
        NomeReduzido: payload.shortName,
        PotencialDeProducao: null,
      };

      const result = await this.post(this.pastureUrl, payloadData);
      const corral = transformerUtils.toCorral(result.data as Curral);
      return corral;
    } catch (_) {
      throw new Error(t("locales:create-corral-error"));
    }
  }

  async updateCorral(
    id: number,
    payload: Pick<Corral, "name" | "shortName" | "retreatId">
  ): Promise<Corral> {
    try {
      const payloadData = {
        Nome: payload.name,
        NomeReduzido: payload.shortName,
        IdRetiro: payload.retreatId,
      };

      const result = await this.put(`${this.pastureUrl}/${id}`, payloadData);
      const corral = transformerUtils.toCorral(result.data as Curral);
      return corral;
    } catch (_) {
      throw new Error(t("locales:update-corral-error"));
    }
  }

  async deleteCorral(id: number): Promise<void> {
    try {
      await this.delete(`${this.pastureUrl}/${id}`);
    } catch (_) {
      throw new Error(t("locales:delete-corral-error"));
    }
  }
}
